import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { X } from 'lucide-react-native';
import { twJoin } from 'tailwind-merge';

import type { DotPhraseMenuItem, DotPhraseMenuProps } from '../model/menu';

import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { TOUCH_TARGET_MIN_H } from '@/util/accessibleTouchTarget';

const HEADING_CLASS = 'font-semibold uppercase tracking-wide text-typography-700';

/**
 * The suggestion list a composer dot phrase opens above the input. Purely
 * presentational: the owning hook decides the rows, the highlight, and what a
 * selection does, so keyboard and touch drive the same state.
 *
 * - The highlighted row carries a `primary-500` bar, not only a tint. The tint
 *   alone reads at about 1.08:1 on the card, so it cannot show which row Enter
 *   will pick; the bar clears 3:1 in both themes.
 * - On web a row selects on press-in as well as press. A Gluestack press whose
 *   `Text` child re-renders mid-press can land its mousedown and mouseup on
 *   different nodes and never fire a click, so press alone can drop the first
 *   pick. The item's `onSelect` absorbs the second call.
 * - The close control grows its own box to 48px. react-native-web ignores
 *   `hitSlop`, so padding the touch area is not enough there.
 * - `secondary` lists the phrases the typed text did not match, so a reader
 *   learns the others exist. `highlightedIndex` runs across both lists, with
 *   the secondary rows numbered after the main ones.
 */
export function DotPhraseMenu({
  heading,
  items,
  highlightedIndex,
  status,
  isLoading = false,
  secondary,
  onDismiss,
}: DotPhraseMenuProps) {
  return (
    <VStack className="mb-2 overflow-hidden rounded-lg border border-outline-300 bg-background-0 shadow-soft-1">
      <HStack className="items-center justify-between border-b border-outline-100 pl-3">
        <Text size="xs" className={HEADING_CLASS}>
          {heading}
        </Text>
        <Pressable
          onPress={onDismiss}
          className={twJoin(
            TOUCH_TARGET_MIN_H,
            'min-w-[48px] items-center justify-center rounded-md data-[hover=true]:bg-background-50'
          )}
          {...getAccessibilityProps({ role: 'button', label: 'Close suggestions' })}>
          <Icon as={X} size="sm" className="text-typography-700" />
        </Pressable>
      </HStack>
      {items.map((item, index) => (
        <DotPhraseRow key={item.key} item={item} isHighlighted={index === highlightedIndex} />
      ))}
      {status ? (
        <HStack className="min-h-12 items-center gap-2 px-3 py-2" accessibilityLiveRegion="polite">
          {isLoading ? <Spinner size="small" /> : null}
          <Text size="sm" className="text-typography-700" aria-live="polite">
            {status}
          </Text>
        </HStack>
      ) : null}
      {secondary && secondary.items.length > 0 ? (
        <>
          <Text
            size="xs"
            className={twJoin(HEADING_CLASS, 'border-y border-outline-100 px-3 py-2')}>
            {secondary.heading}
          </Text>
          {secondary.items.map((item, index) => (
            <DotPhraseRow
              key={item.key}
              item={item}
              isHighlighted={items.length + index === highlightedIndex}
            />
          ))}
        </>
      ) : null}
    </VStack>
  );
}

function DotPhraseRow({
  item,
  isHighlighted,
}: {
  item: DotPhraseMenuItem;
  isHighlighted: boolean;
}) {
  return (
    <Pressable
      onPress={item.onSelect}
      onPressIn={isWeb ? item.onSelect : undefined}
      className={twJoin(
        'min-h-12 justify-center border-l-4 py-2 pl-2 pr-3 web:cursor-pointer data-[hover=true]:bg-background-50',
        isHighlighted ? 'border-primary-500 bg-background-50' : 'border-transparent'
      )}
      accessibilityState={{ selected: isHighlighted }}
      {...getAccessibilityProps({ role: 'button', label: item.accessibilityLabel })}>
      <Text size="sm" numberOfLines={2} className="font-medium text-typography-900">
        {item.title}
      </Text>
      {item.subtitle ? (
        <Text size="xs" numberOfLines={1} className="text-typography-700">
          {item.subtitle}
        </Text>
      ) : null}
    </Pressable>
  );
}
